import {
  PlanConfigModel,
  PlanLimitConfig,
  PlanTrialConfig,
} from "src/app/Services/subscription.service";

const DEFAULT_TRIAL_CONFIG: PlanTrialConfig = {
  planId: "starter",
  days: 14,
  maxGuides: 1,
  allowGuidePacks: false,
  allowAi: false,
  yearlyDiscountPercent: 20,
};

const DEFAULT_PLAN_CONFIG: PlanConfigModel = {
  trial: DEFAULT_TRIAL_CONFIG,
  overage: {
    extraViewPriceGbp: 0.02,
    extraGuidePriceGbp: 15,
    viewsNotifyThresholdPercent: 80,
  },
  plans: {
    starter: {
      maxPublishedFlows: 3,
      maxStepsPerFlow: 10,
      maxMonthlyViews: 2000,
      monthlyCoinAllowance: 80,
      allowOverage: true,
    },
    growth: {
      maxPublishedFlows: 20,
      maxStepsPerFlow: 15,
      maxMonthlyViews: 15000,
      monthlyCoinAllowance: 350,
      allowOverage: true,
    },
  },
};

export function mergePlanConfig(
  config?: PlanConfigModel | null
): PlanConfigModel {
  if (!config?.plans) {
    return DEFAULT_PLAN_CONFIG;
  }

  return {
    overage: {
      ...DEFAULT_PLAN_CONFIG.overage,
      ...(config.overage || {}),
    },
    plans: {
      ...DEFAULT_PLAN_CONFIG.plans,
      ...config.plans,
    },
    trial: {
      ...DEFAULT_TRIAL_CONFIG,
      ...(config.trial || {}),
    },
  };
}

export function getTrialConfig(config?: PlanConfigModel | null): PlanTrialConfig {
  return { ...DEFAULT_TRIAL_CONFIG, ...(config?.trial || {}) };
}

export function formatViewsLimit(limit: number): string {
  return limit.toLocaleString("en-GB");
}

export function formatViewOverageRate(config: PlanConfigModel): string {
  const viewPrice = config.overage?.extraViewPriceGbp ?? 0.02;
  return `£${viewPrice.toFixed(2)}/extra view`;
}

export function formatOverageRates(config: PlanConfigModel): string {
  const viewPrice = config.overage?.extraViewPriceGbp ?? 0.02;
  return `£${viewPrice.toFixed(2)}/extra view`;
}

export function getPlanLimits(
  config: PlanConfigModel,
  planId: "starter" | "growth"
): PlanLimitConfig {
  return (
    config.plans?.[planId] ||
    DEFAULT_PLAN_CONFIG.plans![planId]!
  );
}

export function starterGuideFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "starter");
  return `Up to ${plan.maxPublishedFlows} guides / month (${plan.maxStepsPerFlow} steps each)`;
}

export function growthGuideFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "growth");
  return `Up to ${plan.maxPublishedFlows} guides / month (12-15 steps)`;
}

export function starterViewsFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "starter");
  return `Up to ${formatViewsLimit(plan.maxMonthlyViews)} monthly views`;
}

export function growthViewsFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "growth");
  return `Up to ${formatViewsLimit(plan.maxMonthlyViews)} monthly views`;
}

export function starterCoinsFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "starter");
  return `${plan.monthlyCoinAllowance} AI coins / month included`;
}

export function growthCoinsFeature(config: PlanConfigModel): string {
  const plan = getPlanLimits(config, "growth");
  return `${plan.monthlyCoinAllowance} AI coins / month included`;
}
